import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { OtpChallenge, OtpMethod, Permission, SessionUser } from '@itr/shared';
import { setLocale } from '@itr/shared';
import { api, tokens } from '../api/client';
import { demoRole, fetchDemoSite, type DemoLogin, type DemoSite } from '../api/demo';

/** What `login` resolves to: either signed in, or one more step needed. */
export type LoginOutcome = { status: 'ok' } | (OtpChallenge & { status: 'otp_required' });

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  login: (email: string, password: string, otp?: { code: string; method: OtpMethod }) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
  refreshUser: () => Promise<void>;
  /** Set when the server is a demo site; null on a normal install. */
  demo: DemoSite | null;
  /** Which demo role this session is, when it is one. */
  demoLogin: DemoLogin | null;
  /** Sign in as one of the demo's roles — from the sign-in page, or to switch. */
  enterDemo: (key: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [demo, setDemo] = useState<DemoSite | null>(null);

  const signInAs = useCallback(async (choice: DemoLogin): Promise<boolean> => {
    try {
      const res = await api.post<{ accessToken: string; refreshToken?: string; user: SessionUser }>('/api/auth/login', {
        email: choice.email,
        password: choice.password,
        platform: 'web',
      });
      tokens.set(res.accessToken, res.refreshToken);
      setUser(res.user);
      return true;
    } catch {
      return false;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!tokens.hasSession) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<{ user: SessionUser; profile?: { locale?: string } }>('/api/auth/me');
      // Number, date and currency formatting follows the user's configured
      // locale, never the host OS — otherwise an Arabic-locale machine renders
      // Arabic-Indic digits beside English labels.
      setLocale(me.profile?.locale);
      setUser(me.user);
    } catch {
      // Any failure here means the stored tokens are unusable; drop them rather
      // than leaving the app in a half-authenticated state.
      tokens.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    tokens.onUnauthenticated(() => setUser(null));

    void (async () => {
      const site = await fetchDemoSite();
      if (cancelled) return;
      setDemo(site);

      // A demo visitor who has already picked a role is let straight back in
      // as it. One who has not (a first visit, or after signing out) gets the
      // sign-in page, which is where the roles are offered.
      const chosen = site?.logins.find((choice) => choice.key === demoRole.get());
      if (site && chosen) {
        let resumed = false;
        if (tokens.hasSession) {
          try {
            const me = await api.get<{ user: SessionUser; profile?: { locale?: string } }>('/api/auth/me');
            setLocale(me.profile?.locale);
            setUser(me.user);
            resumed = true;
          } catch {
            tokens.clear();
          }
        }
        if (!resumed) await signInAs(chosen);
        if (cancelled) return;
        setLoading(false);
      } else {
        await refreshUser();
      }

      // On a demo site, a session that stops working means the demo was reset
      // under this tab: every record it is showing is gone. Start again from
      // the dashboard — the reload signs the visitor back in as the same role
      // — rather than leave them on a page for something that no longer exists.
      if (site) {
        tokens.onUnauthenticated(() => {
          if (demoRole.get()) window.location.assign('/');
          else setUser(null);
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshUser, signInAs]);

  const enterDemo = useCallback(
    async (key: string) => {
      const choice = demo?.logins.find((candidate) => candidate.key === key);
      if (!choice) return;
      const switching = tokens.hasSession;
      // Recorded first: requests still in flight as the old role will fail
      // once it is signed out, and the reload that follows must land on the
      // new one.
      demoRole.set(choice.key);
      if (switching) {
        // Hand the old role's session back before taking the new one.
        await api.post('/api/auth/logout', { refreshToken: tokens.refresh ?? undefined }).catch(() => {});
        tokens.clear();
      }
      if (!(await signInAs(choice))) return;
      // Another role sees different pages and different clients; whatever is
      // on screen was fetched as the previous one.
      if (switching) window.location.assign('/');
    },
    [demo, signInAs],
  );

  // Matched on the address the session signed in with: a visitor can also type
  // one of the logins into the ordinary form.
  const demoLogin = useMemo(() => demo?.logins.find((choice) => choice.email.toLowerCase() === user?.email.toLowerCase()) ?? null, [demo, user]);

  const login = useCallback(async (email: string, password: string, otp?: { code: string; method: OtpMethod }): Promise<LoginOutcome> => {
    const res = await api.post<
      { accessToken: string; refreshToken?: string; user: SessionUser } | OtpChallenge
    >('/api/auth/login', {
      email,
      password,
      otp: otp?.code,
      otpMethod: otp?.method,
      platform: 'web',
    });
    if ('otpRequired' in res) return { status: 'otp_required', ...res };
    tokens.set(res.accessToken, res.refreshToken);
    setUser(res.user);
    // Typing one of the demo logins into the form is the same choice as clicking it.
    const typed = demo?.logins.find((choice) => choice.email.toLowerCase() === email.toLowerCase());
    if (typed) demoRole.set(typed.key);
    return { status: 'ok' };
  }, [demo]);

  const logout = useCallback(async () => {
    // Revoke server-side first so a stolen refresh token dies with the session,
    // but never block sign-out on the network.
    // A cookie session has no body token — the server revokes the cookie's.
    await api.post('/api/auth/logout', { refreshToken: tokens.refresh ?? undefined }).catch(() => {});
    demoRole.set(null);
    tokens.clear();
    setUser(null);
  }, []);

  const can = useCallback(
    (permission: Permission) => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const value = useMemo(
    () => ({ user, loading, login, logout, can, refreshUser, demo, demoLogin, enterDemo }),
    [user, loading, login, logout, can, refreshUser, demo, demoLogin, enterDemo],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
